import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalLinks from '../components/LegalLinks';
import { Briefcase, Users, Shield, Building2, ArrowRight } from 'lucide-react';

const portals = [
  { to: '/admin/login', icon: Shield, label: 'Management', desc: 'Admin dashboard & operations', gradient: 'metric-gradient-rose', testId: 'admin-portal' },
  { to: '/careers', icon: Briefcase, label: 'Careers', desc: 'Explore open positions', gradient: 'metric-gradient-emerald', testId: 'careers-portal' },
  { to: '/employee/login', icon: Users, label: 'Employees', desc: 'Team workspace & tools', gradient: 'metric-gradient-amber', testId: 'employee-portal' },
  { to: '/client/login', icon: Building2, label: 'Clients', desc: 'Project tracking & deliverables', gradient: 'metric-gradient-blue', testId: 'client-portal' },
];

export default function HomePage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col" data-testid="home-mobile">
        <div className="flex-1 px-5 py-10">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/hed-icon.png" alt="HED" className="w-10 h-10 rounded-xl" />
              <img src="/girgit-logo.png" alt="Girgit" className="w-10 h-10 rounded-xl" />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight mb-2">Girgit</h1>
            <p className="text-sm text-neutral-400">Creative agency. Enterprise operations.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {portals.map((p) => (
              <Link key={p.to} to={p.to}
                className={`${p.gradient} rounded-2xl p-4 active:scale-95 transition-transform`}
                data-testid={`mobile-${p.testId}`}>
                <div className="w-10 h-10 flex items-center justify-center mb-3 rounded-xl bg-white/20 backdrop-blur-sm">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-0.5">{p.label}</h3>
                <p className="text-[10px] text-white/70 mb-2">{p.desc}</p>
                <span className="text-[10px] font-semibold text-white/90 flex items-center gap-1">
                  Access <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
        <footer className="py-6 px-5 border-t border-white/10">
          <div className="text-center">
            <p className="text-xs text-neutral-600">&copy; {new Date().getFullYear()} Half Eaten Donut</p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col" data-testid="home-desktop">
      <div className="flex-1 flex flex-col justify-center container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img src="/hed-icon.png" alt="HED" className="w-12 h-12 rounded-xl" />
            <div className="w-px h-10 bg-white/20" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-12 h-12 rounded-xl" />
            <span className="text-5xl font-bold text-white tracking-tight">Girgit</span>
          </div>
          <p className="text-neutral-400 max-w-2xl mx-auto text-base">
            India&apos;s first creative agency engineered on enterprise grade operations, real-time metrics, and AI-driven intelligence.
          </p>
        </div>

        <div className="max-w-5xl mx-auto w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {portals.map((p) => (
              <Link key={p.to} to={p.to}
                className={`group ${p.gradient} rounded-2xl p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                data-testid={p.testId}>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                    <p.icon className="w-7 h-7 text-white" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{p.label}</h3>
                <p className="text-sm text-white/70 mb-5">{p.desc}</p>
                <div className="w-full py-2.5 text-center font-semibold text-sm rounded-xl bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors">
                  Access Portal
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src="/hed-icon.png" alt="HED" className="w-5 h-5 rounded-lg" />
            <div className="w-px h-4 bg-white/20" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-5 h-5 rounded-lg" />
            <span className="text-sm font-bold text-white">Girgit</span>
          </div>
          <a href="mailto:media@halfeatendonut.in" className="text-xs text-[#FF6B8A] font-semibold">media@halfeatendonut.in</a>
          <LegalLinks tone="dark" className="mt-3" />
          <p className="text-[11px] text-neutral-600 mt-2">&copy; {new Date().getFullYear()} Half Eaten Donut. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
